import { createWorld, type World } from 'bitecs';
import { EventQueue } from '@src/core/EventQueue';
import { Pools } from '@src/core/Pools';
import { Rng } from '@src/core/Rng';
import { Config } from '@src/core/Config';
import { EnemyKind } from '@src/ecs/components';
import { DoorKind } from '@src/game/arena/RoomLayout';
import { FloorMap } from '@src/game/arena/FloorMap';
import { SpatialHash } from '@src/ecs/SpatialHash';
import { createPlayerInput, MAX_PLAYERS, type PlayerInput } from '@src/input/InputState';

export enum RoomPhase {
  Enter = 0,
  Fight = 1,
  Clear = 2,
  Transition = 3,
  GameOver = 4,
  Victory = 5,
}

export enum BossState {
  None = 0,
  Alive = 1,
  Dead = 2,
  /** Portals are down; the rune circle is charging and the boss lands when it finishes. */
  Incoming = 3,
}

export interface RoomState {
  phase: RoomPhase;
  phaseTime: number;
  fightTime: number;
  tier: number;
  roomIndex: number;
  door: DoorKind;
  /** Small-fry kind this room's portals are selling. Most bursts are this pack. */
  featured: EnemyKind;
  /** Door kind offered behind each of the 4 gates during the Clear phase. */
  doorKinds: Uint8Array;
  gatesOpen: boolean;
  spawnersAlive: number;
  dragonWarned: boolean;
  dragonEid: number;
  /** Floor boss entity while it is on the floor (alive or dying), else -1. */
  bossEid: number;
  /** BossState: none yet, alive, or dead (room may clear). */
  bossState: BossState;
  /** BossKind of the boss guarding this room (archmage on the exit, a champion elsewhere). */
  bossKind: number;
  /** 0..1 progress of the rumble build between the last portal and the boss landing. */
  bossBuild: number;
  /** Rune point the boss will land on. */
  bossX: number;
  bossY: number;
  /** Set by the RoomDirector when the player exits through a gate. */
  chosenGate: number;
  /** Opposite of chosenGate: the doorway the wizard walks into. */
  entrySide: number;
  /** Incoming room sits this far from the origin during a Smash TV walk. */
  shiftX: number;
  shiftY: number;
  /** Camera center; RenderSync copies this onto the ortho camera. */
  panX: number;
  panY: number;
  /** Door kinds for the room being walked into (outgoing kinds stay on doorKinds). */
  nextDoorKinds: Uint8Array;
}

/** Particle emitter kinds consumed by the renderer's ParticlePool. */
export enum ParticleKind {
  MuzzleSparks = 0,
  BoltTrail,
  HitSpark,
  ImpGoo,
  GolemRubble,
  PortalMotes,
  PortalBurst,
  PickupSparkle,
  Embers,
  Dust,
  GateDust,
  Confetti,
  /** Directional ichor / blood with gravity. Tint picks the creature. */
  BloodSpray,
  /** Ivory shards that arc and drop. */
  BoneSpray,
  /** Rising ash that settles. */
  AshFall,
  /** Larger tumbling chunks (limbs, stone, teeth). */
  GibChunk,
  /** Wisps that tear apart and drift. */
  MistTear,
}

/** Floating text kinds for the HUD popup layer. */
export enum PopupKind {
  Score = 0,
  Combo,
  Prize,
  Heal,
}

/** Lightning beam segments for the renderer: x1,y1,x2,y2 per segment. */
export interface BeamBuffer {
  count: number;
  readonly data: Float32Array;
  readonly capacity: number;
}

export interface FxRequests {
  hitStopMs: number;
  shakeX: number;
  shakeY: number;
  rumble: number;
  /** Decal requests: x, y, tint, scale per entry. */
  decalCount: number;
  readonly decals: Float32Array;
  /** Particle emitter requests: kind, x, y, dirX, dirY, power, tint per entry. */
  particleCount: number;
  readonly particles: Float32Array;
  /** Popup requests: kind, value, x, y per entry. */
  popupCount: number;
  readonly popups: Float32Array;
  /** Full-screen flash intensity 0..1, decays in the renderer. */
  flash: number;
}

export const PARTICLE_STRIDE = 7;
export const POPUP_STRIDE = 4;

/** Queue a particle burst for the renderer. Silently drops when the ring is full. `tint` 0 = kind default. */
export function emitParticles(fx: FxRequests, kind: ParticleKind, x: number, y: number, dirX: number, dirY: number, power: number, tint = 0): void {
  const cap = fx.particles.length / PARTICLE_STRIDE;
  if (fx.particleCount >= cap) return;
  const o = fx.particleCount * PARTICLE_STRIDE;
  fx.particles[o] = kind;
  fx.particles[o + 1] = x;
  fx.particles[o + 2] = y;
  fx.particles[o + 3] = dirX;
  fx.particles[o + 4] = dirY;
  fx.particles[o + 5] = power;
  fx.particles[o + 6] = tint;
  fx.particleCount++;
}

export function emitPopup(fx: FxRequests, kind: PopupKind, value: number, x: number, y: number): void {
  const cap = fx.popups.length / POPUP_STRIDE;
  if (fx.popupCount >= cap) return;
  const o = fx.popupCount * POPUP_STRIDE;
  fx.popups[o] = kind;
  fx.popups[o + 1] = value;
  fx.popups[o + 2] = x;
  fx.popups[o + 3] = y;
  fx.popupCount++;
}

export function requestDecal(fx: FxRequests, x: number, y: number, tint: number, scale: number): void {
  const cap = fx.decals.length / 4;
  if (fx.decalCount >= cap) return;
  const o = fx.decalCount * 4;
  fx.decals[o] = x;
  fx.decals[o + 1] = y;
  fx.decals[o + 2] = tint;
  fx.decals[o + 3] = scale;
  fx.decalCount++;
}

export interface GameContext {
  readonly world: World;
  readonly events: EventQueue;
  readonly pools: Pools;
  readonly rng: Rng;
  readonly inputs: PlayerInput[];
  readonly room: RoomState;
  readonly beams: BeamBuffer;
  readonly fx: FxRequests;
  /** Enemy spatial grid, rebuilt each tick by EnemyAISystem and again between collision passes. */
  readonly grid: SpatialHash;
  /** Simulation time in seconds since run start. */
  time: number;
  /** Shared run score (gems and gold are shared between players). */
  score: number;
  /** Total kills this run. */
  kills: number;
  /** Summed spawner-tier scaling used by SpawnerSystem. */
  difficulty: number;
  /** Kill-chain state for the score multiplier. */
  readonly combo: ComboState;
  /** Room grid for the current floor (drives gates and the minimap). */
  readonly floor: FloorMap;
  /** Extra lives left this run. Set from the start-screen tier; Infinity is unlimited. */
  continues: number;
  /** True while the CONTINUE? beat owns the death (sim is paused by main). */
  continuePrompt: boolean;
}

export interface ComboState {
  count: number;
  multiplier: number;
  lastKill: number;
  best: number;
}

export function createGameContext(seed: number): GameContext {
  const inputs: PlayerInput[] = [];
  for (let i = 0; i < MAX_PLAYERS; i++) inputs.push(createPlayerInput());
  return {
    world: createWorld(),
    events: new EventQueue(2048),
    pools: new Pools(),
    rng: new Rng(seed),
    inputs,
    room: {
      phase: RoomPhase.Enter,
      phaseTime: 0,
      fightTime: 0,
      tier: 1,
      roomIndex: 1,
      door: DoorKind.Combat,
      featured: EnemyKind.Imp,
      doorKinds: new Uint8Array(4),
      gatesOpen: false,
      spawnersAlive: 0,
      dragonWarned: false,
      dragonEid: -1,
      bossEid: -1,
      bossState: BossState.None,
      bossKind: 0,
      bossBuild: 0,
      bossX: 0,
      bossY: 0,
      chosenGate: -1,
      entrySide: -1,
      shiftX: 0,
      shiftY: 0,
      panX: 0,
      panY: 0,
      nextDoorKinds: new Uint8Array(4),
    },
    beams: { count: 0, data: new Float32Array(4 * 64), capacity: 64 },
    fx: {
      hitStopMs: 0,
      shakeX: 0,
      shakeY: 0,
      rumble: 0,
      decalCount: 0,
      decals: new Float32Array(4 * 256),
      particleCount: 0,
      particles: new Float32Array(PARTICLE_STRIDE * 512),
      popupCount: 0,
      popups: new Float32Array(POPUP_STRIDE * 64),
      flash: 0,
    },
    grid: new SpatialHash(1.5),
    time: 0,
    score: 0,
    kills: 0,
    difficulty: 1,
    combo: { count: 0, multiplier: 1, lastKill: -Infinity, best: 0 },
    floor: new FloorMap(),
    continues: 0,
    continuePrompt: false,
  };
}

export const MAX_ENTITIES = Config.sim.maxEntities;
